import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type MouseEvent } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { TakiConfig } from '@taki/runtime-config';
import {
  Avatar,
  IconDownload,
  IconFileBookmark,
  ScreenLayout,
  ViewHost,
  useAccesPort,
  useJournalPort,
  useLiaConversationsPort,
  useNotificationsPort,
  usePersonnesPort,
  useScreen,
  type Column,
  type EvenementJournalVue,
  type NotificationView,
  type Option,
  type PersonneVue,
  type ScreenAction,
  type ScreenDoor,
  type ScreenFilterDef,
  type ScreenMenuItem,
  type ScreenSortOption,
  type ViewBinding,
  type LiaSujetView,
} from '@taki/ui';
import { fiche, liste, tableau } from '@taki/ui/views/core';
import { destinationDeNotification, ouvrirCentre } from '../centre/ouvrirCentre.js';
import {
  CATEGORIES_JOURNAL,
  PLAFOND_CHARGE,
  RESULTATS_JOURNAL,
  cibleDEvenement,
  debutDePeriode,
  detailDEvenement,
  filtresDuJournal,
  libelleDAction,
  libelleDeResultat,
  libelleDeSource,
  nomDExport,
  statsDuJournal,
  toutLeFiltre,
  versCsv,
} from '../journal.js';
import {
  STATUTS_ENVOI,
  bilanDeRejeu,
  destinataireDe,
  estRejouable,
  filtresDesEnvois,
  libelleDeCanal,
  libelleDeRubrique,
  libelleDeStatut,
  origineDe,
  phraseDe,
  rubriqueDe,
  statsDesEnvois,
  statutAffiche,
  type EnvoiJournal,
} from '../journal-notifications.js';

export interface JournalScreenProps {
  config: TakiConfig;
  /** Les apps du dock, pour le filtre par app : id et nom affiche. */
  apps: readonly { id: string; label: string }[];
}

/** L'identite de l'ecran pour le moteur (memoire de vue, adresse). */
const APP_ID = 'journal';
const SCREEN_ID = 'journal';

/** Tableau d'abord (on lit un journal en colonnes), Liste, et la fiche d'un evenement. */
const VUES = [tableau, liste, fiche];

/** L'id de la porte des envois de notifications, dans le rail apres les categories. */
const PORTE_NOTIFICATIONS = 'notifications';

/**
 * UNE LIGNE DU JOURNAL : un evenement d'audit, ou un ENVOI de notification
 * (porte « Notifications »). Le moteur est generique : chaque porte declare ses
 * propres bindings, filtres et stats, et ne voit que ses lignes.
 */
type LigneJournal = EvenementJournalVue | EnvoiJournal;

function estEnvoi(ligne: LigneJournal): ligne is EnvoiJournal {
  return (ligne as Partial<EnvoiJournal>).genre === 'notification';
}

/** Les tris de la porte Notifications. */
const TRIS_ENVOIS: ScreenSortOption[] = [
  { key: 'at', label: 'Date' },
  { key: 'destinataire', label: 'Destinataire' },
  { key: 'canal', label: 'Canal' },
];

const OPTIONS_STATUT_ENVOI: Option[] = STATUTS_ENVOI.map((s) => ({ value: s.id, label: s.label, tone: s.ton }));

/** Les tris proposes : des clefs presentes dans les deux bindings de collection. */
const TRIS: ScreenSortOption[] = [
  { key: 'at', label: 'Date' },
  { key: 'title', label: 'Événement' },
  { key: 'qui', label: 'Auteur' },
];

/** Date et heure, en francais, a la seconde. */
const QUAND = new Intl.DateTimeFormat('fr-LU', { dateStyle: 'short', timeStyle: 'medium' });

const TON: Readonly<Record<string, Option['tone']>> = { ok: 'success', denied: 'accent', error: 'danger' };
const OPTIONS_RESULTAT: Option[] = RESULTATS_JOURNAL.map((r) => ({ value: r.id, label: r.label, tone: TON[r.id] ?? 'neutral' }));

/**
 * LE JOURNAL D'AUDIT DE L'ENVIRONNEMENT : l'ecran cache du proprietaire.
 *
 * Route `/journal`, absente du dock, du hub et du menu (decision du fondateur,
 * 04/10/2026). Cache n'est pas protege : la protection est dans les RULES, qui
 * ne laissent lire `auditEvents` qu'au proprietaire actif. L'ecran le dit a
 * tout autre compte sans meme poser la question.
 *
 * ECRAN NATIF (regle G3) : le moteur du socle (`useScreen`) et ses vues
 * (Tableau, Liste, fiche), aucun tableau fait main. Une porte par categorie
 * dans le rail ; le catalogue de filtres (personne, type d'evenement, app,
 * periode, resultat), la recherche et les stats sont ceux du moteur. Le journal
 * n'est pas une famille : chaque porte charge les 12 derniers mois de sa
 * categorie par `useJournalPort` (plafond `PLAFOND_CHARGE`), le moteur filtre,
 * cherche et trie en memoire. Une ligne s'ouvre en fiche, en LECTURE SEULE :
 * aucun champ ne declare `set`, aucune porte ne sait archiver.
 * « Exporter » rejoue le filtre courant en CSV (`docs/JOURNAL.md`).
 *
 * LA PORTE « NOTIFICATIONS » (retour R8, 06/10/2026) : une ligne par envoi
 * (`orgs/{orgId}/notifications`, lu par `NotificationsPort.journalPeriode`),
 * avec ses propres vues, filtres (echecs, statut, canal, destinataire) et
 * stats. Un e-mail du canal console se dit « Simule, non envoye ». Un echec se
 * rejoue par le geste du socle (`passage`, `docs/NOTIFICATIONS.md`).
 */
export function JournalScreen({ config, apps }: JournalScreenProps): JSX.Element {
  const acces = useAccesPort();
  const journal = useJournalPort();
  const notifications = useNotificationsPort();
  const personnesPort = usePersonnesPort();
  const router = useRouter();
  const [personnes, setPersonnes] = useState<PersonneVue[]>([]);
  const [charges, setCharges] = useState<EvenementJournalVue[]>([]);
  const [envois, setEnvois] = useState<EnvoiJournal[]>([]);
  const [tronque, setTronque] = useState(false);
  const [envoisTronques, setEnvoisTronques] = useState(false);
  const [exportEtat, setExportEtat] = useState<string | null>(null);
  const [rejeuEtat, setRejeuEtat] = useState<string | null>(null);
  const [rejeuEnCours, setRejeuEnCours] = useState(false);
  const evenementsRef = useRef<EvenementJournalVue[]>([]);
  const envoisRef = useRef<EnvoiJournal[]>([]);

  const proprietaire = acces?.role === 'owner' && acces.indisponible !== true;
  const pret = acces == null || acces.pret;
  const refuse = pret && (!proprietaire || journal == null);

  useEffect(() => {
    if (!proprietaire || personnesPort == null) return;
    void personnesPort.lister().then(setPersonnes).catch(() => setPersonnes([]));
  }, [proprietaire, personnesPort]);

  /* Le nom d'un auteur : l'annuaire des personnes d'abord, l'adresse ensuite. */
  const nomDe = useMemo(() => {
    const parUid = new Map(personnes.filter((p) => p.userId).map((p) => [p.userId as string, p.displayName]));
    return (ev: EvenementJournalVue): string =>
      (ev.actor.uid ? parUid.get(ev.actor.uid) : undefined) ?? ev.actor.email ?? ev.actor.uid ?? 'Inconnu';
  }, [personnes]);
  const nomDeRef = useRef(nomDe);
  nomDeRef.current = nomDe;
  const photoDe = useMemo(() => {
    const parUid = new Map(personnes.filter((p) => p.userId && p.photoURL).map((p) => [p.userId as string, p.photoURL as string]));
    return (ev: EvenementJournalVue): string | undefined => (ev.actor.uid ? parUid.get(ev.actor.uid) : undefined);
  }, [personnes]);
  const photoDeRef = useRef(photoDe);
  photoDeRef.current = photoDe;

  /* Le nom d'un destinataire, par son adresse ou son uid (WhatsApp, interne). */
  const nommer = useMemo(() => {
    const parCle = new Map<string, string>();
    for (const p of personnes) {
      parCle.set(p.email.toLowerCase(), p.displayName);
      if (p.userId) parCle.set(p.userId, p.displayName);
    }
    return (cle: string): string | undefined => parCle.get(cle.toLowerCase()) ?? parCle.get(cle);
  }, [personnes]);
  const nommerRef = useRef(nommer);
  nommerRef.current = nommer;

  /* Le titre courant d'un sujet de Lia, pour l'origine d'une reponse de Lia :
     les sujets de la personne connectee, par le meme port que la fenetre. */
  const liaPort = useLiaConversationsPort();
  const [sujetsLia, setSujetsLia] = useState<LiaSujetView[]>([]);
  useEffect(() => {
    if (!liaPort) return;
    return liaPort.observer(setSujetsLia, () => setSujetsLia([]));
  }, [liaPort]);
  const titreDeSujetLiaRef = useRef<(conversationId: string) => string | undefined>(() => undefined);
  titreDeSujetLiaRef.current = (conversationId) => sujetsLia.find((sujet) => sujet.conversationId === conversationId)?.titre;

  /* Le nom d'une regle, pour l'origine d'un envoi qu'elle a produit. */
  const regles = notifications?.regles;
  const nomDeRegleRef = useRef<(id: string) => string | undefined>(() => undefined);
  nomDeRegleRef.current = (id) => regles?.find((r) => r.id === id)?.nom;

  /* Une porte = une categorie, chargee sur 12 mois par le port, plafonnee. */
  const charger = useCallback(async (categorie: string): Promise<EvenementJournalVue[]> => {
    if (journal == null) return [];
    const { events, complet } = await toutLeFiltre((f) => journal.lister(f), {
      from: debutDePeriode(365),
      ...(categorie ? { category: categorie } : {}),
    }, PLAFOND_CHARGE);
    evenementsRef.current = events;
    setCharges(events);
    setTronque(!complet);
    return events;
  }, [journal]);

  /* La porte Notifications : les envois des 12 derniers mois, plafonnes. */
  const chargerEnvois = useCallback(async (): Promise<EnvoiJournal[]> => {
    if (notifications == null) return [];
    const vues: NotificationView[] = notifications.journalPeriode
      ? await notifications.journalPeriode(debutDePeriode(365), PLAFOND_CHARGE)
      : await notifications.journal(200);
    const lignes = vues.map((n): EnvoiJournal => ({ ...n, genre: 'notification' }));
    envoisRef.current = lignes;
    setEnvois(lignes);
    setEnvoisTronques(lignes.length >= PLAFOND_CHARGE);
    return lignes;
  }, [notifications]);

  /*
   * UN LIEN D'ORIGINE, sans ouvrir la fiche de la ligne. Meme aiguillage que la
   * cloche (`destinationDeNotification`) : une mention ou une reponse de Lia
   * ouvre le centre de communication sur sa conversation, le reste (une regle,
   * un essai, une fiche) ouvre son adresse.
   */
  const lien = useCallback((libelle: string, n: NotificationView, chemin: string): JSX.Element | string => {
    const destination = n.contexte || n.objet?.type === 'lia' || n.objet?.type === 'conversation'
      ? destinationDeNotification(n.chemin ?? '', n.objet ?? null)
      : chemin ? { chemin } : null;
    if (destination === null) return libelle;
    return (
      <button
        type="button"
        className="text-left text-ink-900 underline decoration-ink-300 underline-offset-2 hover:decoration-ink-900"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          if ('centre' in destination) ouvrirCentre(destination.centre);
          else router.history.push(destination.chemin);
        }}
      >
        {libelle}
      </button>
    );
  }, [router]);
  const lienRef = useRef(lien);
  lienRef.current = lien;

  const bindings = useMemo<ViewBinding<LigneJournal>[]>(() => {
    const ev = (l: LigneJournal) => l as EvenementJournalVue;
    const roles = {
      title: { get: (l: LigneJournal) => libelleDAction(ev(l).action), label: 'Événement' },
      subtitle: { get: (l: LigneJournal) => `${nomDeRef.current(ev(l))} · ${cibleDEvenement(ev(l)) || libelleDeSource(ev(l).actor.source)}` },
      status: { get: (l: LigneJournal) => ev(l).outcome, label: 'Résultat', options: () => OPTIONS_RESULTAT },
      dateStart: { get: (l: LigneJournal) => ev(l).at, label: 'Date' },
    };
    const colonnes: Column<LigneJournal>[] = [
      /* La valeur reste l'instant (elle trie) ; la case dit la date ET l'heure,
         qu'un journal ne peut pas taire. */
      { key: 'at', label: 'Date', value: (l) => ev(l).at, render: (l) => QUAND.format(ev(l).at), sortable: true },
      {
        key: 'qui',
        label: 'Auteur',
        value: (l) => nomDeRef.current(ev(l)),
        render: (l) => (
          <span className="inline-flex items-center gap-2">
            <Avatar size={20} src={photoDeRef.current(ev(l))} initials={nomDeRef.current(ev(l)).charAt(0).toLocaleUpperCase('fr')} className="bg-surface-3 text-ink-700" />
            {nomDeRef.current(ev(l))}
          </span>
        ),
        sortable: true,
      },
      { key: 'cible', label: 'Cible', value: (l) => cibleDEvenement(ev(l)) || null },
      { key: 'detail', label: 'Détail', value: (l) => detailDEvenement(ev(l)) || null },
    ];
    const colonnesFiche: Column<LigneJournal>[] = [
      ...colonnes,
      { key: 'resultat', label: 'Résultat', value: (l) => libelleDeResultat(ev(l).outcome) },
      { key: 'action', label: 'Code', value: (l) => ev(l).action },
      { key: 'categorie', label: 'Catégorie', value: (l) => CATEGORIES_JOURNAL.find((c) => c.id === ev(l).category)?.label ?? ev(l).category },
      { key: 'adresse', label: 'Adresse', value: (l) => ev(l).actor.email },
      { key: 'role', label: 'Rôle', value: (l) => ev(l).actor.role },
      { key: 'source', label: 'Depuis', value: (l) => libelleDeSource(ev(l).actor.source) },
      { key: 'app', label: 'App', value: (l) => ev(l).target?.appId ?? null },
      { key: 'porte', label: 'Porte', value: (l) => ev(l).target?.door ?? null },
      { key: 'reference', label: 'Référence', value: (l) => ev(l).id },
    ];
    return [
      { type: 'core.tableau', roles, columns: colonnes, options: { titleLabel: 'Événement', statusLabel: 'Résultat' } },
      { type: 'core.liste', roles, columns: colonnes },
      {
        type: 'core.fiche',
        roles,
        columns: colonnesFiche,
        /* LECTURE SEULE : aucune colonne ne declare `set`. Toutes les clefs
           sont placees, une colonne qu'aucune carte ne cite ne se rend pas. */
        options: {
          cards: [
            { title: "L'événement", keys: ['at', 'resultat', 'categorie', 'action'] },
            { title: 'Auteur', keys: ['qui', 'adresse', 'role', 'source'] },
            { title: 'Cible', keys: ['cible', 'app', 'porte', 'reference'] },
            { title: 'Détail', keys: ['detail'], layout: 'full' },
          ],
        },
      },
    ];
  }, []);

  /*
   * LES VUES DE LA PORTE NOTIFICATIONS : une ligne par envoi. Le titre est le
   * sujet du message ; le statut est le statut AFFICHE (un e-mail du canal
   * console se dit « Simulé, non envoyé ») ; l'origine porte son lien.
   */
  const bindingsEnvois = useMemo<ViewBinding<LigneJournal>[]>(() => {
    const n = (l: LigneJournal) => l as EnvoiJournal;
    const origine = (l: LigneJournal) => origineDe(n(l), nomDeRegleRef.current, titreDeSujetLiaRef.current);
    const roles = {
      title: { get: (l: LigneJournal) => n(l).sujet || '(sans sujet)', label: 'Message' },
      subtitle: { get: (l: LigneJournal) => `${destinataireDe(n(l), nommerRef.current)} · ${libelleDeCanal(n(l).canal)} · ${origine(l).libelle}` },
      status: { get: (l: LigneJournal) => statutAffiche(n(l)), label: 'Statut', options: () => OPTIONS_STATUT_ENVOI },
      dateStart: { get: (l: LigneJournal) => n(l).createdAt, label: 'Date' },
    };
    const colonnes: Column<LigneJournal>[] = [
      { key: 'at', label: 'Date', value: (l) => n(l).createdAt, render: (l) => (n(l).createdAt ? QUAND.format(n(l).createdAt as Date) : ''), sortable: true },
      { key: 'destinataire', label: 'Destinataire', value: (l) => destinataireDe(n(l), nommerRef.current), sortable: true },
      { key: 'canal', label: 'Canal', value: (l) => libelleDeCanal(n(l).canal), sortable: true },
      { key: 'rubrique', label: 'Rubrique', value: (l) => libelleDeRubrique(rubriqueDe(n(l))) },
      { key: 'origine', label: 'Origine', value: (l) => origine(l).libelle, render: (l) => lienRef.current(origine(l).libelle, n(l), origine(l).chemin) },
      { key: 'erreur', label: 'Erreur', value: (l) => (statutAffiche(n(l)) === 'simule' ? null : phraseDe(n(l))) },
    ];
    const colonnesFiche: Column<LigneJournal>[] = [
      ...colonnes,
      { key: 'statut', label: 'Statut', value: (l) => libelleDeStatut(statutAffiche(n(l))) },
      { key: 'phrase', label: 'Explication', value: (l) => phraseDe(n(l)) },
      { key: 'adresse', label: 'Adresse', value: (l) => n(l).destinataireEmail ?? n(l).destinataire },
      { key: 'sujet', label: 'Sujet', value: (l) => n(l).sujet || null },
      { key: 'tentatives', label: 'Tentatives', value: (l) => n(l).tentatives },
      { key: 'prochaine', label: 'Prochaine tentative', value: (l) => n(l).prochaineTentative ?? null, render: (l) => (n(l).prochaineTentative ? QUAND.format(n(l).prochaineTentative as Date) : 'Aucune') },
      { key: 'retours', label: 'Retours du fournisseur', value: (l) => (n(l).evenements ?? []).join(', ') || null },
      { key: 'reference', label: 'Référence', value: (l) => n(l).id },
    ];
    return [
      { type: 'core.tableau', roles, columns: colonnes, options: { titleLabel: 'Message', statusLabel: 'Statut' } },
      { type: 'core.liste', roles, columns: colonnes },
      {
        type: 'core.fiche',
        roles,
        columns: colonnesFiche,
        options: {
          cards: [
            { title: "L'envoi", keys: ['at', 'statut', 'canal', 'rubrique', 'tentatives', 'prochaine'] },
            { title: 'Destinataire', keys: ['destinataire', 'adresse'] },
            { title: 'Origine', keys: ['origine', 'sujet', 'reference'] },
            { title: 'Explication', keys: ['phrase', 'erreur', 'retours'], layout: 'full' },
          ],
        },
      },
    ];
  }, []);

  /* Le catalogue : personnes, types, apps, periodes, resultats presents. */
  const filtres = useMemo<ScreenFilterDef<LigneJournal>[]>(
    () => filtresDuJournal(charges, { personnes, apps, nomDe })
      .map((f) => ({ id: f.id, label: f.label, match: (l: LigneJournal) => !estEnvoi(l) && (f.match?.(l) ?? false) })),
    [charges, personnes, apps, nomDe],
  );
  /* Celui des envois : echecs, statuts, canaux, destinataires presents. */
  const filtresEnvois = useMemo<ScreenFilterDef<LigneJournal>[]>(
    () => filtresDesEnvois(envois, nommer)
      .map((f) => ({ id: f.id, label: f.label, match: (l: LigneJournal) => estEnvoi(l) && (f.match?.(l) ?? false) })),
    [envois, nommer],
  );

  const portes: ScreenDoor<LigneJournal>[] = [
    ...CATEGORIES_JOURNAL.map((c): ScreenDoor<LigneJournal> => ({
      id: c.id || 'tout',
      label: c.label,
      load: () => charger(c.id),
      get: async (id: string) => evenementsRef.current.find((ev) => ev.id === id)
        ?? (await charger(c.id)).find((ev) => ev.id === id) ?? null,
      filters: filtres,
      stats: (vivants: LigneJournal[]) => statsDuJournal(vivants.filter((l): l is EvenementJournalVue => !estEnvoi(l)), tronque),
      searchPlaceholder: 'Rechercher un événement, une personne, une cible',
    })),
    {
      id: PORTE_NOTIFICATIONS,
      label: 'Notifications',
      load: chargerEnvois,
      get: async (id: string) => {
        const brut = id.replace(/^n-/, '');
        return envoisRef.current.find((n) => n.id === brut) ?? (await chargerEnvois()).find((n) => n.id === brut) ?? null;
      },
      bindings: bindingsEnvois,
      filters: filtresEnvois,
      stats: (vivants: LigneJournal[]) => statsDesEnvois(vivants.filter(estEnvoi), envoisTronques),
      searchPlaceholder: 'Rechercher un destinataire, un sujet, une origine',
    },
  ];

  const screen = useScreen<LigneJournal>({
    appId: APP_ID,
    screenId: SCREEN_ID,
    load: async () => [],
    getId: (l) => (estEnvoi(l) ? `n-${l.id}` : l.id),
    types: VUES,
    bindings,
    defaultView: 'core.tableau',
    defaultSort: [{ key: 'at', dir: 'desc' }],
    doors: portes,
    defaultSection: 'tout',
    reloadKey: refuse ? 'refuse' : 'ouvert',
  });
  const surEnvois = screen.door.id === PORTE_NOTIFICATIONS;

  /* Un filtre actif que la porte ne propose plus (personne absente de la
     nouvelle categorie) : on le retire, l'etat suit le catalogue (G10). */
  const { activeFilterIds, removeFilter } = screen;
  const catalogue = surEnvois ? filtresEnvois : filtres;
  useEffect(() => {
    for (const id of activeFilterIds) if (!catalogue.some((f) => f.id === id)) removeFilter(id);
  }, [activeFilterIds, catalogue, removeFilter]);

  const exporter = async (): Promise<void> => {
    const parId = new Map(evenementsRef.current.map((ev) => [ev.id, ev]));
    const lignes = screen.orderedIds.map((id) => parId.get(id)).filter((ev): ev is EvenementJournalVue => ev !== undefined);
    const url = URL.createObjectURL(new Blob([versCsv(lignes)], { type: 'text/csv;charset=utf-8' }));
    const ancre = document.createElement('a');
    ancre.href = url;
    ancre.download = nomDExport(config.orgId);
    ancre.click();
    URL.revokeObjectURL(url);
    setExportEtat(`${lignes.length} ligne${lignes.length > 1 ? 's' : ''} exportée${lignes.length > 1 ? 's' : ''}`);
  };

  /*
   * « REJOUER » : le geste du socle, le passage de la file (`notifier`,
   * `{ passage: true }`, owner ou admin), qui rejoue les echecs DUS. Le moteur
   * garde son attente croissante : la phrase dit ce qui a ete rejoue, ou
   * l'heure de la prochaine tentative.
   */
  const rejouer = async (prochaine: Date | null): Promise<void> => {
    if (notifications == null) return;
    setRejeuEnCours(true);
    try {
      const bilan = await notifications.passage();
      setRejeuEtat(bilanDeRejeu(bilan.sweeper, prochaine));
      screen.reload();
      /* La phrase est un retour, pas un nouveau libelle : le bouton redevient
         « Rejouer » quelques secondes plus tard. */
      setTimeout(() => setRejeuEtat(null), 6000);
    } catch (cause) {
      setRejeuEtat(cause instanceof Error ? cause.message : String(cause));
      setTimeout(() => setRejeuEtat(null), 6000);
    } finally {
      setRejeuEnCours(false);
    }
  };
  const echecs = envois.filter(estRejouable);
  const prochaineDesEchecs = echecs
    .map((n) => n.prochaineTentative ?? null)
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

  const actions: ScreenAction[] = surEnvois
    ? (echecs.length > 0 && notifications?.canManage !== false
      ? [{
          id: 'rejouer',
          label: rejeuEtat ?? `Rejouer les échecs (${echecs.length})`,
          disabled: rejeuEnCours || screen.status.loading,
          onClick: () => void rejouer(prochaineDesEchecs),
        }]
      : [])
    : [{
        id: 'exporter',
        label: exportEtat ?? 'Exporter',
        icon: <IconDownload size={15} />,
        disabled: screen.status.loading || screen.orderedIds.length === 0,
        onClick: () => void exporter(),
      }];

  /* Sur la fiche d'un envoi en echec : le meme geste, a cote de la sortie. */
  const ouvert = screen.openId?.startsWith('n-') ? envoisRef.current.find((n) => `n-${n.id}` === screen.openId) : undefined;
  const gestesDeFiche: ScreenAction[] | undefined = ouvert && estRejouable(ouvert)
    ? [{
        id: 'rejouer-envoi',
        label: rejeuEtat ?? 'Rejouer',
        disabled: rejeuEnCours,
        onClick: () => void rejouer(ouvert.prochaineTentative ?? null),
      }]
    : undefined;

  const menuItems: ScreenMenuItem[] = [
    ...CATEGORIES_JOURNAL.map((c) => ({ id: c.id || 'tout', label: c.label })),
    { id: PORTE_NOTIFICATIONS, label: 'Notifications' },
  ];
  const libelleRetour = screen.views.find((vue) => vue.id === screen.lastCollectionViewId)?.label;

  if (refuse || !pret) {
    return (
      <ScreenLayout
        appName="Journal"
        appIcon={<IconFileBookmark size={18} />}
        title="Journal"
        loading={!pret}
        empty={refuse}
        emptyTitle="Réservé au propriétaire"
        emptyHint="Le journal de l'environnement ne s'ouvre qu'au propriétaire."
      />
    );
  }

  return (
    <ScreenLayout
      appName="Journal"
      appIcon={<IconFileBookmark size={18} />}
      menuItems={menuItems}
      activeMenuId={screen.section ?? 'tout'}
      onMenuSelect={(id) => { setRejeuEtat(null); screen.setSection(id); }}
      title="Journal"
      onSearchChange={screen.setSearch}
      searchValue={screen.query.search}
      searchPlaceholder={screen.door.searchPlaceholder}
      headerStats={screen.door.stats}
      quickActions={actions}
      viewActions={screen.viewActions}
      tools={screen}
      sortOptions={surEnvois ? TRIS_ENVOIS : TRIS}
      views={screen.views}
      activeViewId={screen.viewId}
      onViewSelect={screen.setView}
      onRecordExit={() => screen.openRecord(null)}
      {...(libelleRetour !== undefined ? { recordExitLabel: libelleRetour } : {})}
      {...(gestesDeFiche !== undefined ? { recordActions: gestesDeFiche } : {})}
      saveState={screen.saveState}
      onRetrySave={screen.retrySave}
      scrollRef={screen.scrollRef}
    >
      <ViewHost
        state={screen}
        errorTitle={surEnvois ? "Les envois n'ont pas pu être lus" : "Le journal n'a pas pu être lu"}
        retryLabel="Réessayer"
        emptyTitle={surEnvois ? 'Aucun envoi' : 'Aucun événement'}
        emptyHint={surEnvois ? 'Aucune notification envoyée sur les 12 derniers mois.' : 'Rien sur les 12 derniers mois dans cette catégorie.'}
        notFoundTitle={surEnvois ? 'Envoi introuvable' : 'Événement introuvable'}
        notFoundHint="Il a pu être effacé à la fin de sa durée de conservation (12 mois)."
      />
    </ScreenLayout>
  );
}
